import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import Map from "@/components/Map";
import CreateGuideForm from "@/components/create-guide/CreateGuideForm";
import LoadingOverlay from "@/components/create-guide/LoadingOverlay";
import { useAppSelector, useAppDispatch } from "@/lib/hooks";
import { addGuide, GuideRequest } from "@/features/guides/guidesSlice";
import {
  startGenerating,
  generatingComplete,
  generatingError,
  resetCreation
} from "@/features/guideCreation/guideCreationSlice";
import { updateProfile } from "@/features/user/userSlice";
import { createGuide, options, user } from "@/api/apiService";
import { USER } from "@/api/apiConfig";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import {fetchBalanceSuccess} from "@/features/tokens/tokensSlice.ts";

interface Attraction {
  id: number;
  name: string;
}

interface Transport {
  id: number;
  name: string;
}

interface OptionsResponse {
  attractions: Attraction[];
  transports: Transport[];
}

const CreateGuide = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>("");
  const [attractionOptions, setAttractionOptions] = useState<Attraction[]>([]);
  const [transportationOptions, setTransportationOptions] = useState<Transport[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const {
    country,
    city,
    duration,
    attractions,
    transports,
    day_period,
    payment_preference,
    physical_activity,
    public: isPublic,
    generating,
    error
  } = useAppSelector(state => state.guideCreation);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const data: OptionsResponse = await options();
        if (data) {
          setAttractionOptions(data.attractions);
          setTransportationOptions(data.transports);
        }
      } catch (error) {
        console.error('Failed to fetch options:', error);
        toast.error('Failed to load options');
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions().then();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(startGenerating());

    try {
      const guideData: GuideRequest = {
        country,
        city,
        duration: Number(duration),
        attractions,
        transports,
        day_period,
        payment_preference,
        physical_activity,
        public: isPublic,
      };

      const response = await createGuide(guideData);

      if (response) {
        dispatch(addGuide(response));
        dispatch(generatingComplete());
        dispatch(resetCreation());

        try {
          const userData = await user();
          if (userData && userData.user?.tokens !== undefined) {
            dispatch(fetchBalanceSuccess(userData.user.tokens));
          }
        } catch (userError) {
          console.error('Failed to reload user data:', userError);
        }

        toast.success("Guide created successfully", {
          description: `Your guide for ${city}, ${country} has been created.`
        });
        navigate(`/guides/${response.id}`);
      } else {
        throw new Error("Failed to create guide");
      }
    } catch (err) {
      console.error('Guide creation error:', err);
      let errorMessage = "Failed to create guide";

      // Handle specific error cases
      if (err.response?.data?.message) {
        const backendMessage = err.response.data.message;
        if (backendMessage.includes("API key") || backendMessage.includes("Unauthorized")) {
          errorMessage = "Service temporarily unavailable. Please try again later.";
        } else {
          errorMessage = backendMessage;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      dispatch(generatingError(errorMessage));
      toast.error("Guide creation failed", {
        description: errorMessage
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8 mt-16">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold mb-8">Create New Guide</h1>

          <div className="grid lg:grid-cols-3 gap-8">
            <Card className="lg:col-span-1 p-6 space-y-6">
              <CreateGuideForm
                onSubmit={handleSubmit}
                error={error}
                generating={generating}
                onCountryCodeChange={setSelectedCountryCode}
                attractionOptions={attractionOptions}
                transportationOptions={transportationOptions}
                loadingOptions={loadingOptions}
              />
            </Card>

            <Card className="lg:col-span-2 min-h-[600px] relative overflow-hidden">
              <Map
                className="absolute inset-0"
                selectedCountry={country}
                countryCode={selectedCountryCode}
              />
            </Card>
          </div>
        </div>
      </main>
      <Footer />

      <LoadingOverlay
        isVisible={generating}
        country={country}
        city={city}
      />
    </div>
  );
};

export default CreateGuide;
